// useCameraStatus Hook - 2ECOMP Fleet Management
// Monitors camera/stream status from M7S server

import {
 useState, useEffect, useCallback, useRef
} from 'react';
import { getStreamListUrl, getStreamPath } from '../utils/streamUrls';
import { useCameraServer } from './useCameraServer';

/**
 * Custom hook to monitor camera stream status
 * Polls M7S server to check active streams
 *
 * @param {string|number} deviceId - Optional device ID to filter
 * @param {Object} options - Configuration options
 * @returns {Object} Status information and methods
 */
export const useCameraStatus = (deviceId = null, options = {}) => {
  const {
    pollInterval = 30000,    // 30 seconds default
    autoStart = true,
    onStreamStart = null,
    onStreamEnd = null,
  } = options;

  const cameraServer = useCameraServer();

  const [streams, setStreams] = useState([]);
  const [activeChannels, setActiveChannels] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);

  const previousStreamsRef = useRef([]);
  const intervalRef = useRef(null);
  const mountedRef = useRef(true);

  const initialFetchDoneRef = useRef(false);

  // Fetch stream list from M7S API
  const fetchStreams = useCallback(async () => {
    if (!mountedRef.current) return;

    // Only show loading spinner on first fetch, not on polls
    if (!initialFetchDoneRef.current) setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(getStreamListUrl(cameraServer.baseUrl));

      if (!response.ok) {
        throw new Error(`HTTP error: ${response.status}`);
      }

      const data = await response.json();
      // ZLM returns { code: 0, data: [{app, stream, ...}] }
      // Normalize to path format for compatibility
      const streamList = (data.data || []).map((s) => ({
        ...s,
        path: `${s.app}/${s.stream}`,
      }));

      if (!mountedRef.current) return;

      // Filter streams by device if deviceId provided
      let filteredStreams = streamList;
      if (deviceId) {
        const prefix = `live/${deviceId}-`;
        filteredStreams = streamList.filter((s) => s.path?.startsWith(prefix));
      }

      // Extract active channels for the device (shallow compare to avoid re-renders)
      if (deviceId) {
        const channels = filteredStreams
          .map((s) => {
            const match = s.stream?.match(/^[^-]+-(\d+)$/);
            return match ? parseInt(match[1], 10) : null;
          })
          .filter((ch) => ch !== null && ch >= 1 && ch <= 8)
          .sort((a, b) => a - b);
        setActiveChannels((prev) => {
          if (prev.length === channels.length && prev.every((ch, i) => ch === channels[i])) return prev;
          return channels;
        });
      }

      // Shallow compare streams by path to avoid re-renders
      setStreams((prev) => {
        if (prev.length === filteredStreams.length && prev.every((s, i) => s.path === filteredStreams[i]?.path)) return prev;
        return filteredStreams;
      });
      setLastUpdate(new Date());

      // Detect stream changes for callbacks
      const previousPaths = new Set(previousStreamsRef.current.map((s) => s.path));
      const currentPaths = new Set(filteredStreams.map((s) => s.path));

      // New streams
      if (onStreamStart) {
        filteredStreams.forEach((stream) => {
          if (!previousPaths.has(stream.path)) {
            onStreamStart(stream);
          }
        });
      }

      // Ended streams
      if (onStreamEnd) {
        previousStreamsRef.current.forEach((stream) => {
          if (!currentPaths.has(stream.path)) {
            onStreamEnd(stream);
          }
        });
      }

      previousStreamsRef.current = filteredStreams;
    } catch (err) {
      console.error('[CameraStatus] Error fetching streams:', err);
      if (mountedRef.current) {
        setError(err.message);
      }
    } finally {
      if (mountedRef.current) {
        initialFetchDoneRef.current = true;
        setIsLoading(false);
      }
    }
  }, [deviceId, onStreamStart, onStreamEnd, cameraServer.baseUrl]);

  // Check if a specific channel is active
  const isChannelActive = useCallback(
    (channel) => activeChannels.includes(channel),
    [activeChannels]
  );

  // Check if device has any active stream
  const hasActiveStream = useCallback(() => {
    if (deviceId) {
      return activeChannels.length > 0;
    }
    return streams.length > 0;
  }, [deviceId, activeChannels, streams]);

  // Get stream info for a specific channel
  const getStreamInfo = useCallback(
    (channel) => {
      if (!deviceId) return null;
      const path = getStreamPath(deviceId, channel);
      return streams.find((s) => s.path === path);
    },
    [deviceId, streams]
  );

  // Get all devices with active streams
  const getDevicesWithStreams = useCallback(() => {
    const deviceMap = new Map();

    streams.forEach((stream) => {
      const match = stream.stream?.match(/^([^-]+)-(\d+)$/);
      if (match) {
        const [, devId, channel] = match;
        if (!deviceMap.has(devId)) {
          deviceMap.set(devId, { deviceId: devId, channels: [] });
        }
        deviceMap.get(devId).channels.push(parseInt(channel, 10));
      }
    });

    return Array.from(deviceMap.values());
  }, [streams]);

  // Start polling
  const startPolling = useCallback(() => {
    if (intervalRef.current) return;

    fetchStreams(); // Initial fetch
    intervalRef.current = setInterval(fetchStreams, pollInterval);
  }, [fetchStreams, pollInterval]);

  // Stop polling
  const stopPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  // Manual refresh
  const refresh = useCallback(() => {
    return fetchStreams();
  }, [fetchStreams]);

  // Setup/cleanup
  useEffect(() => {
    mountedRef.current = true;

    if (autoStart) {
      startPolling();
    }

    return () => {
      mountedRef.current = false;
      stopPolling();
    };
  }, [autoStart, startPolling, stopPolling]);

  // Restart polling when deviceId changes
  useEffect(() => {
    if (autoStart && intervalRef.current) {
      stopPolling();
      startPolling();
    }
  }, [deviceId]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    // State
    streams,
    activeChannels,
    isLoading,
    error,
    lastUpdate,

    // Computed
    hasActiveStream: hasActiveStream(),
    streamCount: streams.length,

    // Methods
    isChannelActive,
    getStreamInfo,
    getDevicesWithStreams,
    refresh,
    startPolling,
    stopPolling,
  };
};

/**
 * Simplified hook for checking single device camera status
 * @param {string|number} deviceId
 * @returns {Object} Simple status
 */
export const useDeviceCameraStatus = (deviceId) => {
  const { activeChannels, hasActiveStream, isLoading, refresh } = useCameraStatus(deviceId, {
    pollInterval: 30000,
    autoStart: Boolean(deviceId),
  });

  return {
    hasCamera: hasActiveStream,
    activeChannels,
    channelCount: activeChannels.length,
    isLoading,
    refresh,
  };
};

export default useCameraStatus;
